import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Plus, Minus, BadgeDollarSign, Trash2, MessageCircle, Trophy, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReferralsPanel = () => {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/referrals`);
      setReferrals(res.data);
    } catch {
      toast.error("Error cargando referidos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const update = async (code, body) => {
    try {
      const res = await axios.put(`${API}/admin/referrals/${code}`, body);
      setReferrals(prev => prev.map(r => (r.code === code ? res.data : r)));
    } catch {
      toast.error("No se pudo actualizar");
    }
  };

  const remove = async (code) => {
    if (!window.confirm("Eliminar este codigo de referido?")) return;
    try {
      await axios.delete(`${API}/admin/referrals/${code}`);
      setReferrals(prev => prev.filter(r => r.code !== code));
      toast.success("Referido eliminado");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const totalOwed = referrals.reduce((s, r) => s + r.owed_mxn, 0);
  const totalSales = referrals.reduce((s, r) => s + r.sales, 0);
  const leaderboard = [...referrals].filter(r => r.sales > 0 || r.visits > 0).sort((a, b) => b.sales - a.sales || b.whatsapp_clicks - a.whatsapp_clicks || b.visits - a.visits).slice(0, 5);
  const medal = ["text-amber-400", "text-gray-300", "text-amber-700", "text-[#A0A0A0]", "text-[#A0A0A0]"];

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#B026FF] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="referrals-section">
      <div className="grid grid-cols-3 gap-4">
        <div className="glass-card p-5 text-center" data-testid="referral-stat-count">
          <p className="text-2xl font-oswald font-bold text-white">{referrals.length}</p>
          <p className="text-xs text-[#A0A0A0] uppercase tracking-wider mt-1">Codigos</p>
        </div>
        <div className="glass-card p-5 text-center" data-testid="referral-stat-sales">
          <p className="text-2xl font-oswald font-bold text-[#00FF66]">{totalSales}</p>
          <p className="text-xs text-[#A0A0A0] uppercase tracking-wider mt-1">Ventas Referidas</p>
        </div>
        <div className="glass-card p-5 text-center" data-testid="referral-stat-owed">
          <p className="text-2xl font-oswald font-bold text-amber-400">${totalOwed}</p>
          <p className="text-xs text-[#A0A0A0] uppercase tracking-wider mt-1">Por Pagar (MXN)</p>
        </div>
      </div>

      {leaderboard.length > 0 && (
        <div className="glass-card p-5" data-testid="referral-leaderboard">
          <h3 className="font-oswald text-lg font-bold text-white uppercase flex items-center gap-2 mb-4"><Trophy className="w-5 h-5 text-amber-400" /> Top Referidos</h3>
          <div className="space-y-2">
            {leaderboard.map((r, i) => (
              <div key={r.code} className="flex items-center gap-3 bg-[#050505] rounded-xl px-4 py-3 border border-white/5" data-testid={`leaderboard-row-${i}`}>
                <span className={`font-oswald text-xl font-bold w-6 ${medal[i]}`}>#{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-semibold truncate">{r.name} <span className="text-[#A0A0A0] font-mono text-xs ml-1">{r.code}</span></p>
                  <p className="text-xs text-[#A0A0A0]">{r.visits} visitas &bull; {r.whatsapp_clicks} clics WA</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-[#00FF66] font-bold">{r.sales} venta{r.sales !== 1 ? "s" : ""}</p>
                  <p className="text-xs text-[#A0A0A0]">ha ganado ${r.sales * 50}</p>
                </div>
                <a href={`${window.location.origin}/referido/${r.code}`} target="_blank" rel="noreferrer" className="text-[#B026FF] hover:text-white" title="Ver su pagina de ganancias" data-testid={`leaderboard-link-${r.code}`}><ExternalLink className="w-4 h-4" /></a>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-[#A0A0A0]">
        Cuando un cliente te escriba con "Referido por: CODIGO", busca el codigo aqui y presiona <span className="text-white">+1 venta</span>. Al pagar los $50, presiona <span className="text-white">Pagado</span>.
      </p>

      {referrals.length === 0 ? (
        <div className="text-center py-20 glass-card">
          <Gift className="w-10 h-10 text-[#A0A0A0] mx-auto mb-3" />
          <p className="text-[#A0A0A0]">Nadie ha creado un enlace de referido todavia</p>
        </div>
      ) : (
        <div className="glass-card overflow-hidden divide-y divide-white/5">
          {referrals.map(r => (
            <div key={r.code} className="p-4 flex flex-wrap items-center gap-4" data-testid={`referral-row-${r.code}`}>
              <div className="min-w-[8rem]">
                <p className="font-oswald text-lg font-bold text-[#00FF66] tracking-widest">{r.code}</p>
                <p className="text-sm text-white">{r.name}</p>
                <a href={`https://wa.me/${r.whatsapp.length === 10 ? "52" + r.whatsapp : r.whatsapp}`} target="_blank" rel="noreferrer" className="text-xs text-[#25D366] flex items-center gap-1 hover:underline">
                  <MessageCircle className="w-3 h-3" /> {r.whatsapp}
                </a>
                <a href={`${window.location.origin}/referido/${r.code}`} target="_blank" rel="noreferrer" className="text-xs text-[#B026FF] flex items-center gap-1 hover:underline mt-0.5" data-testid={`referral-earnings-link-${r.code}`}>
                  <ExternalLink className="w-3 h-3" /> Pagina de ganancias
                </a>
              </div>
              <div className="flex gap-4 text-xs text-[#A0A0A0] flex-1">
                <span>Visitas: <span className="text-white">{r.visits}</span></span>
                <span>Clics WA: <span className="text-white">{r.whatsapp_clicks}</span></span>
                <span>Ventas: <span className="text-[#00FF66] font-bold">{r.sales}</span></span>
                <span>Debes: <span className={`font-bold ${r.owed_mxn > 0 ? "text-amber-400" : "text-white"}`} data-testid={`referral-owed-${r.code}`}>${r.owed_mxn}</span></span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => update(r.code, { sales: Math.max(r.sales - 1, r.paid_sales) })} className="p-2 rounded-lg border border-white/10 text-[#A0A0A0] hover:text-white" title="-1 venta" data-testid={`referral-minus-${r.code}`}><Minus className="w-4 h-4" /></button>
                <button onClick={() => update(r.code, { sales: r.sales + 1 })} className="px-3 py-2 rounded-lg bg-[#00FF66] text-[#050505] text-xs font-bold flex items-center gap-1" data-testid={`referral-plus-${r.code}`}><Plus className="w-3 h-3" /> 1 venta</button>
                <button onClick={() => update(r.code, { paid_sales: r.sales })} disabled={r.owed_mxn === 0} className="px-3 py-2 rounded-lg bg-amber-400 text-[#050505] text-xs font-bold flex items-center gap-1 disabled:opacity-30" data-testid={`referral-paid-${r.code}`}><BadgeDollarSign className="w-3 h-3" /> Pagado</button>
                <button onClick={() => remove(r.code)} className="p-2 rounded-lg border border-white/10 text-[#A0A0A0] hover:text-red-400" data-testid={`referral-delete-${r.code}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
